import { useEffect, useState } from "react";
import { X, Printer, Download } from "lucide-react";
import { inr, STATUS_META } from "../lib";
import { api } from "@/lib/api";

/**
 * EstimatePreview — full-page A4-like preview matching the final PDF.
 * Includes toggles to show/hide optional items, tax breakup, part numbers,
 * internal descriptions, and garage bank details.
 */
export function EstimatePreview({ est, onClose, onPrint, onDownload }) {
  const [garage, setGarage] = useState({});
  const [opts, setOpts] = useState({
    show_part_numbers: true,
    show_tax_breakup: true,
    show_optional: true,
    show_bank: false,
    show_descriptions: true,
  });

  useEffect(() => {
    api.get("/workshop").then((r) => setGarage(r.data || {})).catch(() => {});
  }, []);

  const t = est.totals || {};
  const cust = est.customer_snapshot || {};
  const veh = est.vehicle_snapshot || {};

  const drawLine = (l, kind) => {
    const desc = kind === "parts" ? l.name : kind === "labor" ? l.task_name : l.charge_type;
    const skipOptional = !opts.show_optional && l.optional;
    if (skipOptional) return null;
    const total = l.computed?.line_total ?? Number((l.qty || 0) * (l.unit_price || 0) * (1 + (l.tax_pct || 0) / 100));
    return (
      <tr key={l.id} className={`border-b border-[#F1FAEE] ${l.optional ? "opacity-70 italic" : ""}`}>
        <td className="py-2">
          <div className="font-medium">
            {desc}
            {l.optional && <span className="ml-2 text-[10px] font-semibold text-[#457b9d] uppercase">Optional</span>}
          </div>
          {opts.show_descriptions && l.description && (
            <div className="text-[10.5px] text-[#667085]">{l.description}</div>
          )}
          {opts.show_part_numbers && kind === "parts" && l.sku && (
            <div className="text-[10px] text-[#667085] font-mono">{l.sku}</div>
          )}
        </td>
        <td className="text-right py-2 font-mono">{l.qty}</td>
        <td className="text-right py-2 font-mono">{inr(l.unit_price)}</td>
        <td className="text-right py-2 font-mono text-[10px]">{l.tax_pct}%</td>
        <td className="text-right py-2 font-mono font-semibold">{inr(total)}</td>
      </tr>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex flex-col" data-testid="est-preview-dialog" onClick={onClose}>
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 bg-white shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="text-[#a8dadc] hover:text-[#e63946] p-1">
            <X size={16} />
          </button>
          <div>
            <div className="text-[13px] font-semibold text-[#1d3557]">Preview</div>
            <div className="text-[10.5px] text-[#667085] font-mono">{est.number || "DRAFT"}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Toggle label="Optional items" v={opts.show_optional} on={(v) => setOpts({ ...opts, show_optional: v })} />
          <Toggle label="Descriptions" v={opts.show_descriptions} on={(v) => setOpts({ ...opts, show_descriptions: v })} />
          <Toggle label="Part numbers" v={opts.show_part_numbers} on={(v) => setOpts({ ...opts, show_part_numbers: v })} />
          <Toggle label="Tax breakup" v={opts.show_tax_breakup} on={(v) => setOpts({ ...opts, show_tax_breakup: v })} />
          <button
            type="button" onClick={onPrint}
            data-testid="preview-print"
            className="h-9 px-3 rounded-lg border border-[#E4E7EC] bg-white text-[12px] font-semibold text-[#1d3557] inline-flex items-center gap-1"
          >
            <Printer size={12} /> Print
          </button>
          <button
            type="button" onClick={onDownload}
            data-testid="preview-download"
            className="h-9 px-3 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12px] font-semibold inline-flex items-center gap-1"
          >
            <Download size={12} /> Download PDF
          </button>
        </div>
      </div>

      {/* A4 preview */}
      <div className="flex-1 overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto bg-white shadow-2xl" style={{ width: "210mm", minHeight: "297mm", padding: "16mm" }}>
          {/* Header */}
          <div className="border-b-2 border-[#1d3557] pb-3 mb-4 flex items-start justify-between">
            <div>
              <div className="text-[22px] font-bold text-[#1d3557]">{garage.name || "Your Garage"}</div>
              <div className="text-[10.5px] text-[#667085] mt-0.5">{garage.address || ""}</div>
              <div className="text-[10.5px] text-[#667085]">
                {[garage.phone, garage.email].filter(Boolean).join(" · ")}
              </div>
              {garage.gstin && <div className="text-[10.5px] text-[#667085] mt-0.5">GSTIN: {garage.gstin}</div>}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#e63946]">Estimate / Quotation</div>
              <div className="text-[16px] font-bold text-[#1d3557] font-mono">{est.number || "DRAFT"}</div>
              <div className="text-[10px] text-[#667085] mt-1">Date: {est.estimate_date}</div>
              <div className="text-[10px] text-[#667085]">Valid until: {est.valid_until}</div>
              <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase ${STATUS_META[est.status]?.chip}`}>
                {STATUS_META[est.status]?.label}
              </span>
            </div>
          </div>

          {/* Customer + Vehicle */}
          <div className="grid grid-cols-2 gap-4 mb-4 text-[12px]">
            <div>
              <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#457b9d] border-b border-[#e63946] mb-1 pb-0.5">Bill To</div>
              <div className="font-semibold text-[#1d3557]">{cust.name || "—"}</div>
              <div className="text-[#667085]">{cust.phone}</div>
              {cust.email && <div className="text-[#667085]">{cust.email}</div>}
              {cust.address && <div className="text-[#667085]">{cust.address}</div>}
              {cust.gstin && <div className="text-[#667085]">GSTIN: {cust.gstin}</div>}
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#457b9d] border-b border-[#e63946] mb-1 pb-0.5">Vehicle</div>
              <div className="font-semibold text-[#1d3557] font-mono">{veh.reg_number || "—"}</div>
              <div className="text-[#667085]">{[veh.make, veh.model, veh.variant].filter(Boolean).join(" ")}</div>
              <div className="text-[#667085]">
                {[veh.fuel_type, veh.year, veh.odometer ? `${veh.odometer} km` : null].filter(Boolean).join(" · ")}
              </div>
              {veh.vin && <div className="text-[#667085] font-mono text-[10.5px]">VIN: {veh.vin}</div>}
            </div>
          </div>

          {/* Complaints */}
          {est.complaints && est.complaints.length > 0 && (
            <div className="mb-4">
              <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#457b9d] border-b border-[#e63946] mb-1 pb-0.5">Customer Complaints</div>
              <ol className="list-decimal ml-5 text-[11.5px] text-[#1d3557] space-y-0.5">
                {est.complaints.map((c) => <li key={c.id}>{c.text}</li>)}
              </ol>
            </div>
          )}

          {/* Line items */}
          <PreviewTable title="Parts" lines={est.parts} drawLine={(l) => drawLine(l, "parts")} />
          <PreviewTable title="Labour Charges" lines={est.labor} drawLine={(l) => drawLine(l, "labor")} />
          <PreviewTable title="Other Charges" lines={est.other_charges} drawLine={(l) => drawLine(l, "other")} />

          {/* Totals */}
          <div className="mt-4 flex justify-end">
            <table className="text-[12px] w-64">
              <tbody>
                <tr><td className="text-[#667085] py-1">Parts</td><td className="text-right font-mono">{inr(t.parts_subtotal)}</td></tr>
                <tr><td className="text-[#667085] py-1">Labour</td><td className="text-right font-mono">{inr(t.labor_subtotal)}</td></tr>
                <tr><td className="text-[#667085] py-1">Other</td><td className="text-right font-mono">{inr(t.charges_subtotal)}</td></tr>
                <tr className="border-t border-[#F1FAEE]"><td className="py-1 font-semibold">Gross Subtotal</td><td className="text-right font-mono font-semibold">{inr(t.gross_subtotal)}</td></tr>
                {Number(t.overall_discount) > 0 && <tr className="text-[#e63946]"><td className="py-1">Discount</td><td className="text-right font-mono">− {inr(t.overall_discount)}</td></tr>}
                <tr><td className="text-[#667085] py-1">Taxable</td><td className="text-right font-mono">{inr(t.taxable_value)}</td></tr>
                {opts.show_tax_breakup && (
                  Number(t.igst) > 0
                    ? <tr><td className="text-[#667085] py-1">IGST</td><td className="text-right font-mono">{inr(t.igst)}</td></tr>
                    : <>
                        <tr><td className="text-[#667085] py-1">CGST</td><td className="text-right font-mono">{inr(t.cgst)}</td></tr>
                        <tr><td className="text-[#667085] py-1">SGST</td><td className="text-right font-mono">{inr(t.sgst)}</td></tr>
                      </>
                )}
                {Number(t.rounding_adjustment) !== 0 && <tr><td className="text-[#667085] py-1 text-[10.5px]">Rounding</td><td className="text-right font-mono text-[10.5px]">{inr(t.rounding_adjustment)}</td></tr>}
                <tr className="border-t-2 border-[#1d3557] bg-[#F1FAEE]">
                  <td className="py-2 font-bold">Grand Total</td>
                  <td className="text-right py-2 font-mono font-bold text-[16px] text-[#1d3557]">{inr(t.grand_total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Terms */}
          {est.terms && est.terms.length > 0 && (
            <div className="mt-6">
              <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#457b9d] border-b border-[#e63946] mb-1 pb-0.5">Terms & Conditions</div>
              <ol className="list-decimal ml-5 text-[10px] text-[#1d3557] space-y-0.5">
                {est.terms.map((tt, i) => <li key={i}>{tt}</li>)}
              </ol>
            </div>
          )}

          {est.customer_notes && (
            <div className="mt-4">
              <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#457b9d] mb-1">Notes</div>
              <div className="text-[11px] text-[#1d3557] whitespace-pre-wrap">{est.customer_notes}</div>
            </div>
          )}

          {/* Signature block */}
          <div className="mt-10 grid grid-cols-2 gap-8 text-[10.5px]">
            <div>
              <div className="border-t border-[#1d3557] pt-1 text-center">Customer Approval</div>
            </div>
            <div>
              <div className="border-t border-[#1d3557] pt-1 text-center">For {garage.name || "Workshop"}</div>
            </div>
          </div>

          <div className="mt-6 text-[9px] text-[#667085] text-center">
            This estimate is valid until {est.valid_until}. Prices are subject to actual work required.
          </div>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, v, on }) {
  return (
    <label className="inline-flex items-center gap-1 text-[11px] text-[#1d3557]">
      <input type="checkbox" checked={!!v} onChange={(e) => on(e.target.checked)} />
      {label}
    </label>
  );
}

function PreviewTable({ title, lines = [], drawLine }) {
  if (!lines || lines.length === 0) return null;
  const rendered = lines.map(drawLine).filter(Boolean);
  if (rendered.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#457b9d] border-b border-[#e63946] mb-1 pb-0.5">{title}</div>
      <table className="w-full text-[11.5px]">
        <thead>
          <tr className="text-[9.5px] uppercase text-[#667085] border-b border-[#E4E7EC]">
            <th className="text-left py-1.5">Item</th>
            <th className="text-right py-1.5 w-14">Qty</th>
            <th className="text-right py-1.5 w-20">Rate</th>
            <th className="text-right py-1.5 w-12">Tax</th>
            <th className="text-right py-1.5 w-24">Amount</th>
          </tr>
        </thead>
        <tbody>{rendered}</tbody>
      </table>
    </div>
  );
}
